import type { QueryClient } from '@tanstack/react-query';
import { getInquiries } from '@/api/inquiries';
import { getLlmConfig } from '@/api/llmConfig';
import { preferencesStore } from '@/features/preferences';
import { deviceLanguage } from '@/features/preferences/PreferencesSession';
import { loadCurrentUserId } from '@/features/chat/runs/runOwner';
import { currentUserKey } from '@/utils/session';

/**
 * Defect D-06: the Q&A page waited for three rounds of requests before its
 * largest paint: GET /saas/me/info, then config, the Q&A list, /llm/me/info
 * and preferences, then the newest Q&A's details. The second round needs only
 * the stored token, so with a stored session it starts here, at boot, in
 * parallel with /saas/me/info. Each request is the one the page makes anyway
 * (same react-query key, the memoized user id, the preferences store, a list
 * the first list load takes over), so the request count does not grow. A 401
 * reaches the same handler as before, only sooner.
 */

/** A prefetched list older than this is not used: the page asks again. */
const LIST_FRESH_MS = 15_000;

let prefetchedList: {
  userKey: string;
  at: number;
  request: ReturnType<typeof getInquiries>;
} | null = null;

/** The Q&A page, where these requests are made. */
const QA_PATHS = new Set(['/', '/dashboard', '/dashboard/']);

/**
 * Whether to prefetch at boot: on the Q&A page, and not on the Auth0
 * redirect (`?code=&state=` or `?error=`), where a stored token may be
 * another user's than the one signing in. A token in the URL (the SaaS
 * entry) is fine: main.tsx adopts it first and keys the prefetch by it.
 */
export const shouldBootPrefetch = (
  location: Pick<Location, 'pathname' | 'search'>,
) => {
  if (!QA_PATHS.has(location.pathname)) return false;
  const search = new URLSearchParams(location.search);
  const auth0Redirect =
    (search.has('code') && search.has('state')) || search.has('error');
  return !auth0Redirect;
};

export const startBootPrefetch = (
  queryClient: QueryClient,
  userKey: string,
) => {
  void queryClient.prefetchQuery({
    queryKey: ['llm-config'],
    queryFn: getLlmConfig,
    staleTime: Infinity,
  });
  void loadCurrentUserId();
  void preferencesStore.start(userKey, deviceLanguage());
  const request = getInquiries({ projects_id: '' });
  // Taken over by the page; a failure there is handled where it is used.
  request.catch(() => undefined);
  prefetchedList = { userKey, at: Date.now(), request };
};

/**
 * The list request started at boot, once, for the signed-in user while it
 * is fresh; null otherwise (the caller requests it).
 */
export const takePrefetchedInquiries = (now = Date.now()) => {
  const prefetched = prefetchedList;
  prefetchedList = null;
  if (
    !prefetched ||
    prefetched.userKey !== currentUserKey() ||
    now - prefetched.at > LIST_FRESH_MS
  ) {
    return null;
  }
  return prefetched.request;
};
